import {computed, inject, Injectable, signal} from '@angular/core';
import {AbstractControl, FormGroup, ValidationErrors} from '@angular/forms';
import {MatSnackBar, MatSnackBarConfig} from '@angular/material/snack-bar';
import {Observable, tap, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {Router} from '@angular/router';
import {CustomSnackbarComponent} from '../components/custom-snackbar/custom-snackbar.component';

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  router = inject(Router);

  private readonly AUTH_TOKEN_KEY = 'accesshub/token';

  private forgotPasswordSuccess = signal<boolean>(false);
  private signupSuccess = signal<boolean>(false);
  private verifyEmailSuccess = signal<boolean>(false);
  private timeLeft = signal<number>(30);
  private timerInterval: any;

  setSignupSuccess(value: boolean) {
    this.signupSuccess.set(value)
  }

  getSignupSuccess() {
    return this.signupSuccess.asReadonly();
  }

  setVerifyEmailSuccess(value: boolean) {
    this.verifyEmailSuccess.set(value);
  }

  getVerifyEmailSuccess() {
    return this.verifyEmailSuccess.asReadonly();
  }

  setForgotPasswordSuccess(value: boolean) {
    this.forgotPasswordSuccess.set(value);
  }

  getForgotPasswordSuccess() {
    return this.forgotPasswordSuccess.asReadonly();
  }

  timeLeftSignal = computed(() => this.timeLeft());

  constructor(private snackBar: MatSnackBar, private http: HttpClient) {
  }

  setItemToLocalStorage(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  getItemFromLocalStorage(key: string): string | null {
    const value = localStorage.getItem(key);
    return value;
  }

  removeItemFromLocalStorage(key: string): void {
    localStorage.removeItem(key);
  }

  setAuthenticationToken(token: string): void {
    this.setItemToLocalStorage(this.AUTH_TOKEN_KEY, token);
  }

  clearAuthenticationToken(): void {
    this.removeItemFromLocalStorage(this.AUTH_TOKEN_KEY);
  }

  isAuthenticated(): boolean {
    return !!this.getItemFromLocalStorage(this.AUTH_TOKEN_KEY);
  }

  checkFormValidation(form: FormGroup, errorMessageMap: any, currentField?: string) {
    const errorMessages = {};
    const formControls = form.controls || null;

    if (currentField) {
      const errors = form.controls[currentField].errors;
      const firstErrorType = errors ? Object.keys(errors)[0] : '';
      // @ts-ignore
      errorMessages[currentField] = errorMessageMap[currentField] && errorMessageMap[currentField][firstErrorType] || '';
      return errorMessages;
    }

    for (const eachControlName in formControls) {
      const isControlValid = form.controls[eachControlName].valid;
      if (!isControlValid) {
        const errors = form.controls[eachControlName].errors;
        const firstErrorType = errors ? Object.keys(errors)[0] : null;
        if (firstErrorType) {
          form.controls[eachControlName].markAsTouched();
          // @ts-ignore
          errorMessages[eachControlName] = errorMessageMap[eachControlName][firstErrorType];
        }
      }
    }
    return errorMessages;
  }

  validatePassword(control: AbstractControl): ValidationErrors | null {
    const password = control.value;
    let errors = {};

    const containsSpace = new RegExp('\\s');

    if (containsSpace.test(password)) {
      errors = {
        ...errors,
        containsSpaceCheckFailed: true
      }
      return errors;
    }

    const containsLowercase = new RegExp('[a-z]');
    if (!containsLowercase.test(password)) {
      errors = {
        ...errors,
        lowerCaseCheckFailed: true
      }
      return errors;
    }

    const containsUppercase = new RegExp('[A-Z]');
    if (!containsUppercase.test(password)) {
      errors = {
        ...errors,
        upperCaseCheckFailed: true
      }
      return errors;
    }

    const containsNumber = new RegExp('[0-9]');
    if (!containsNumber.test(password)) {
      errors = {
        ...errors,
        numberCheckFailed: true
      }
      return errors;
    }

    const containsSpecialChar = new RegExp('[!@#$%^&*()\\\\/\\[\\]\-_=+{}|?>.<,:;~`\'÷×₹€¥"]');
    if (!containsSpecialChar.test(password)) {
      errors = {
        ...errors,
        specialCharCheckFailed: true
      }
      return errors;
    }

    return null;
  }

  logout(): void {
    // Get all keys from localStorage
    const keys = Object.keys(localStorage);

    // Filter and remove keys starting with 'accesshub'
    keys.forEach(key => {
      if (key.startsWith('accesshub')) {
        localStorage.removeItem(key);
      }
    });

    this.router.navigate(['/']);
  }

  refreshToken(refreshEndpoint?: string): Observable<any> {
    const refreshToken = this.getItemFromLocalStorage('accesshub/refresh-token');

    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    const endpoint = refreshEndpoint || `${environment.apiUrl}/v1/refresh-token`;

    return this.http.post<any>(endpoint, {refreshToken}).pipe(
      tap(response => {
        // Store the new tokens
        this.setItemToLocalStorage('accesshub/token', response.token);
        this.setItemToLocalStorage('accesshub/refresh-token', response.refreshToken);
      }),
      catchError(error => {
        // If refresh token is invalid, log the user out
        this.logout();
        return throwError(() => error);
      })
    );
  }

  notification(message: string, type: 'success' | 'danger' | 'warning' = 'success', actionText?: string, duration = 2000) {
    const config: MatSnackBarConfig = {
      duration,
      verticalPosition: 'top',
      horizontalPosition: 'end',
      panelClass: [type]
    };

    // Use the custom snackbar component
    this.snackBar.openFromComponent(CustomSnackbarComponent, {
      ...config,
      data: { message, type }
    });
  }

  displayErrorsWithDelay(errors: any[], type: 'success' | 'danger' | 'warning' = 'danger'): void {
    const notificationDuration = 2000; // 2 seconds (default duration)
    const delayBetweenNotifications = 600; // 0.6 seconds delay after each notification closes

    errors.forEach((error, index) => {
      // Calculate delay based on previous notifications
      // Each notification takes (duration + delay) milliseconds
      const delay = index * (notificationDuration + delayBetweenNotifications);

      setTimeout(() => {
        this.notification(
          error.message || 'An error occurred',
          type
        );
      }, delay);
    });
  }

  startTimer(): void {
    this.timeLeft.set(30);
    this.timerInterval = setInterval(() => {
      this.timeLeft.update(value => {
        if (value <= 0) {
          clearInterval(this.timerInterval);
          return 0;
        }
        return value - 1;
      });
    }, 1000);
  }

  stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  resetTimer(): void {
    this.stopTimer();
    this.timeLeft.set(30);
  }
}
